/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { RefObject } from 'react'
import { View, Text, Icon, Radiobutton, Pressable } from '@components/atoms'
import { grey, blue } from '@mui/material/colors'

import { MenuTreeNodeType } from '@redux/index'

export type CustomDataType = {
	fileType: string
	fileSize: string
}

interface PropsType {
	node: MenuTreeNodeType
	hasChildren: boolean
	childrenNodeList: MenuTreeNodeType[]
	depth: number
	isOpen: boolean
	handleRef?: RefObject<any>
	onToggle: (id: MenuTreeNodeType['id']) => void
	showCheckBox: boolean
	showRadiobutton: boolean
	hideObject: boolean
	selectedRadio: string | number
	disableDragging: boolean
	showDragHandle: boolean
	onRadioChange: (value: string | number) => void
	onNodeClick?: (node: MenuTreeNodeType) => void
	showRootChevronButton: boolean
	selectedMenuId?: string
	nodeRightAccessory?: (node: MenuTreeNodeType) => JSX.Element
	className?: string
	style?: React.CSSProperties
}

export const DndTreeNode = (props: PropsType) => {
	const { droppable } = props.node

	// variables
	const indent = React.useMemo(() => props.depth * 24, [])
	const childrenNodeLength: number = props.childrenNodeList.length ?? 0

	const handleToggle = React.useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			props.onToggle(props.node.id)
		},
		[props.onToggle, props.node.id],
	)

	const onNodeClick = React.useCallback(() => {
		if (props.onNodeClick !== undefined) {
			props.onNodeClick(props.node)
		}
	}, [props.onNodeClick, props.node.id])

	return (
		<>
			{props.hideObject && !droppable ? (
				<></>
			) : (
				<View
					css={[style, { paddingInlineStart: indent }, { ...props.style }]}
					className={`tree-node ${props.className ? props.className : ''} ${
						props.selectedMenuId === props.node.id ? 'selected' : ''
					}`}
				>
					{!props.disableDragging ? (
						<View
							className="drag-handle"
							css={!props.showDragHandle ? { width: 0 } : null}
							ref={props.handleRef}
							data-testid={`drag-handle-${props.node.id}`}
						>
							{props.showDragHandle ? (
								<Icon name="drag-horizontal-variant" className="nodeDragHandleIcon" />
							) : null}
						</View>
					) : null}

					{props.showRootChevronButton && (
						<Pressable
							className="nodeChevronButton"
							onClick={props.hasChildren ? handleToggle : () => {}}
						>
							<View
								className={`expandIconWrapper ${
									props.isOpen && childrenNodeLength > 0 ? 'isOpen' : ''
								} ${!props.hasChildren ? 'disabled' : ''}`}
							>
								{props.node.droppable ? (
									<View>
										<Icon name="menu-right" />
									</View>
								) : null}
							</View>
						</Pressable>
					)}

					<Pressable className="nodeButton" onClick={onNodeClick}>
						<View>
							<Icon
								name={
									droppable
										? 'folder'
										: props.node.icon
										? props.node.icon
										: 'application-brackets-outline'
								}
								className="nodeIcon"
							/>
						</View>

						<View className="labelGridItem">
							<Text i18n={props.node.i18n}>{props.node.text}</Text>
						</View>

						{props.node.droppable ? (
							<Text className="childrenNodeCount">{childrenNodeLength}</Text>
						) : null}
					</Pressable>

					{props.showCheckBox ? (
						<View>
							<Radiobutton
								value={props.node.id}
								onRadioChange={props.onRadioChange}
								selectedValue={props.selectedRadio}
							/>
						</View>
					) : props.nodeRightAccessory ? (
						<>{props.nodeRightAccessory(props.node)}</>
					) : null}
				</View>
			)}
		</>
	)
}

const style = css({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	height: '2rem',
	width: '100%',
	borderRadius: '5px',

	'&:first': {
		borderTop: '0px solid',
	},

	'.nodeDragHandleIcon': {
		color: grey[400],
	},

	'.nodeChevronButton': {
		height: '100%',
	},

	'.nodeButton': {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		width: '100%',
		height: '100%',

		'.nodeIcon': {
			color: grey[400],
		},

		'.labelGridItem': {
			paddingInlineStart: '8px',
			marginRight: '0.5rem',
		},

		'.childrenNodeCount': {
			color: grey[400],
		},
	},

	'.expandIconWrapper': {
		alignItems: 'center',
		fontSize: 0,
		cursor: 'pointer',
		display: 'flex',
		height: '24px',
		justifyContent: 'center',
		width: '24px',
		transition: 'transform linear .1s',
		transform: 'rotate(0deg)',

		'&.isOpen': {
			transform: 'rotate(90deg)',
		},

		'&.disabled': {
			opacity: 0.2,
		},
	},

	'.configButton': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '2rem',
		height: '2rem',

		'.configIcon': {
			color: grey[500],
		},
	},

	'&.selected': {
		background: '#f1f9ff',
		color: blue[700],

		'.nodeIcon': {
			color: blue[700],
		},

		'.labelGridItem': {
			color: blue[700],
		},
	},
})
